/**
 * Firebase Storage Service
 * 
 * Provides access to Firebase Cloud Storage functionality for the application.
 * This service handles cloud storage operations including:
 * - Retrieving documents and JSON data from storage
 * - Downloading files from storage URLs
 * - Supporting region-specific document retrieval
 * - Managing user profile pictures
 * 
 * Uses Firebase Storage modular API to interact with Firebase Cloud Storage.
 * Includes helper methods for download operations with proper error handling.
 */
import { Injectable } from '@angular/core';
import { 
    Storage,
    getStorage, 
    ref, 
    getDownloadURL
} from '@angular/fire/storage';

import { UtilsService } from '../lib/utils.service';

import type {
    TRandomWords,
    TRegionDocument
} from '@shared/models';

@Injectable({
    'providedIn': 'root'
})
export class FirebazeStorageService {
    private storage: Storage;

    constructor(private utilsService: UtilsService) {
        this.storage = getStorage();
    }

    /**
     * Gets region-specific document from storage
     * @param regionCode The region code to retrieve document for
     * @returns The region document data
     */
    public async getRegionDocument(regionCode: string): Promise<TRegionDocument> {
        try {
            const storageRef = ref(this.storage, `zipcodes/${regionCode}.json`);
            const url = await getDownloadURL(storageRef);
            const blobFile = await this.download(url);
            
            const text = await blobFile.text();
            const json = JSON.parse(text);
            const doc = json as TRegionDocument;
            return doc;
        } catch (err) {
            const errorMessage = this.utilsService.extractError(err);
            throw new Error(`Failed to retrieve region document: ${errorMessage}`);
        }
    }

    /**
     * Gets random words document from storage
     * @returns The random words data
     */
    public async getRandomWordDocument(): Promise<TRandomWords> {
        try {
            const storageRef = ref(this.storage, 'randomWords/en-us_randomWords.json');
            const url = await getDownloadURL(storageRef);
            const response = await fetch(url);
            
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            
            const data = await response.json();
            return data as TRandomWords;
        } catch (err) {
            const errorMessage = this.utilsService.extractError(err);
            throw new Error(`Failed to retrieve random word document: ${errorMessage}`);
        }
    }

    /**
     * Downloads a file from a URL and returns it as a blob
     * @param url The URL to download from
     * @returns A Promise resolving to the downloaded blob
     */
    private async download(url: string): Promise<Blob> {
        try {
            const response = await fetch(url);
            if (!response.ok) {
                const errorMessage = await response.text();
                const msg = 'Error in firebazeStorage.download; err = ' + errorMessage;
                throw new Error(msg);
            }
            const blob = await response.blob();
            return blob;
        } catch (err) {
            const errorMessage = this.utilsService.extractError(err);
            const msg = 'Error in firebazeStorage.download; err = ' + errorMessage;
            throw new Error(msg);
        }
    }

    /**
     * Placeholder for saving user profile pictures
     * @param profilePictureData The profile picture data
     * @param uid The user ID
     */
    public async saveUserProfilePicture(profilePictureData: string, uid: string): Promise<void> {
        if (!profilePictureData || !uid) {
            return;
        }
        
        try {
            const storageRef = ref(this.storage, `profilePictures/${uid}`);
            // Implementation would go here using uploadString or uploadBytes
            // console.log('Profile picture would be saved for user:', uid);
        } catch (err) {
            const errorMessage = this.utilsService.extractError(err);
            throw new Error(`Failed to save user profile picture: ${errorMessage}`);
        }
    }

    // Commented methods could be reimplemented using modular API if needed
}